import { supabase, refreshSession as refreshSupabaseSession } from '@/integrations/supabase/client';
import { User } from '@/types';
import { env } from '@/config/env';

export const authService = {
  // Get the current user from the Supabase session
  getCurrentUser: async () => {
    try {
      // First try to get the current session
      const { data, error } = await supabase.auth.getSession();
      
      if (error) {
        if (env.DEV) console.error('Error getting session');
        return null;
      }
      
      if (!data.session) {
        // No session found, try to refresh
        if (env.DEV) console.log('No session found, attempting to refresh');
        const refreshedSession = await refreshSupabaseSession();
        
        if (!refreshedSession) {
          if (env.DEV) console.log('Session refresh failed, user not authenticated');
          return null;
        }
        
        if (env.DEV) console.log('Session refreshed successfully');
        return refreshedSession.user as unknown as User;
      }
      
      // Session exists, return the user
      return data.session.user as unknown as User;
    } catch (error) {
      if (env.DEV) console.error('Get current user error');
      return null;
    }
  },

  // Refresh the current session
  refreshSession: async () => {
    try {
      const refreshedSession = await refreshSupabaseSession();
      return !!refreshedSession;
    } catch (error) {
      if (env.DEV) console.error('Session refresh error');
      return false;
    }
  },

  // Sign in with email and password
  signIn: async (email: string, password: string) => {
    try {
      // Use the direct supabase auth method for better compatibility
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (error) {
        throw error;
      }
      
      // Store minimal user data safely
      if (data?.user) {
        localStorage.setItem('authUser', JSON.stringify({
          id: data.user.id,
          email: data.user.email,
          email_confirmed: !!data.user.email_confirmed_at
        }));
        
        // Force refresh the subscription status after login
        try {
          const { refreshSubscription } = await import('@/contexts/SubscriptionContext');
          await refreshSubscription(true); // Force refresh
          console.log('Subscription status refreshed after login');
        } catch (subError) {
          console.error('Error refreshing subscription status after login:', subError);
          // Don't fail the login if subscription refresh fails
        }
      }
      
      return data;
    } catch (error) {
      if (env.DEV) console.error('Sign in error');
      throw error;
    }
  },

  // Sign out the current user
  signOut: async () => {
    try {
      // Clear any locally stored auth data
      localStorage.removeItem('authUser');
      localStorage.removeItem('sb-lcevfqxsypgtrquujsug-auth');
      sessionStorage.clear();
      
      // Call the supabase signout method
      const { error } = await supabase.auth.signOut({
        scope: 'local' // Only clear local session, not global logout
      });
      
      if (error) {
        throw error;
      }
      
      return true;
    } catch (error) {
      if (env.DEV) console.error('Sign out error');
      throw error;
    }
  },

  // Check if the user is authenticated
  isAuthenticated: async () => {
    try {
      console.log('Checking if user is authenticated');
      const { data } = await supabase.auth.getSession();
      
      if (!data.session) {
        // Try to refresh the session
        console.log('No session found, attempting to refresh');
        const refreshedSession = await refreshSupabaseSession();
        const isAuth = !!refreshedSession;
        console.log('Session refresh result:', isAuth ? 'authenticated' : 'not authenticated');
        return isAuth;
      }
      
      console.log('User is authenticated with valid session');
      return true;
    } catch (error) {
      console.error('Auth check error:', error);
      return false;
    }
  },
  
  // Verify a password reset token
  verifyPasswordResetToken: async (token: string, email?: string) => {
    try {
      console.log('Verifying password reset token');
      
      // Attempt to verify the OTP token
      // If we have an email, include it in the verification
      const verifyParams: any = {
        token,
        type: 'recovery'
      };
      
      // Always include email if provided, as it's required for some token verifications
      if (email) {
        verifyParams.email = email;
        console.log(`Including email in token verification: ${email}`);
      } else {
        console.log('No email provided for token verification');
      }
      
      const { data, error } = await supabase.auth.verifyOtp(verifyParams);
      
      if (error) {
        console.error('Error verifying password reset token:', error);
        
        // Check if we need an email for verification
        if (error.message && 
            (error.message.includes('email address') || 
             error.message.includes('email is required') ||
             error.message.includes('missing email') ||
             error.message.includes('invalid login credentials'))) {
          return { 
            valid: false, 
            error, 
            needsEmail: true,
            message: 'Please provide your email address to verify this password reset link.'
          };
        }
        
        // Check for expired or invalid token
        if (error.message.includes('expired') || error.message.includes('invalid')) {
          return {
            valid: false,
            error,
            message: 'This password reset link has expired or is invalid. Please request a new one.'
          };
        }
        
        throw error;
      }
      
      console.log('Password reset token verified successfully');
      return { 
        valid: true, 
        data,
        message: 'Token verified successfully. You can now set a new password.'
      };
    } catch (error) {
      console.error('Token verification failed:', error);
      
      // Handle specific error cases
      const errorMessage = error instanceof Error ? error.message : 'Failed to verify token';
      
      return { 
        valid: false, 
        error: errorMessage,
        message: errorMessage.includes('expired') ? 
          'This password reset link has expired. Please request a new one.' :
          'Failed to verify password reset link. Please try again.'
      };
    }
  },
  
  // Check subscription status
  checkSubscriptionStatus: async () => {
    try {
      console.log('Checking subscription status');
      const { refreshSubscription } = await import('@/contexts/SubscriptionContext');
      const status = await refreshSubscription(true);
      console.log('Subscription status:', status);
      return status;
    } catch (error) {
      console.error('Error checking subscription status:', error);
      return null;
    }
  },
  
  // Reset password with email
  resetPassword: async (email: string) => {
    try {
      console.log('Sending password reset email to:', email);
      
      // Ensure we have the correct app domain with proper formatting
      let appUrl = env.VITE_APP_URL || window.location.origin;
      // Remove trailing slash if present
      if (appUrl.endsWith('/')) {
        appUrl = appUrl.slice(0, -1);
      }
      
      const redirectUrl = `${appUrl}/reset-password`;
      console.log('Using redirect URL for password reset:', redirectUrl);
      
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: redirectUrl,
      });

      if (error) {
        console.error('Error from Supabase when sending reset email:', error);
        
        // Handle rate limiting errors specifically
        if (error.message.includes('rate_limit') || error.name === 'RateLimitError' || error.message.includes('wait')) {
          throw {
            message: 'For security purposes, please wait a minute before requesting another reset email.',
            code: 'rate_limit',
            originalError: error
          };
        }
        
        throw error;
      }
      
      console.log('Password reset email sent successfully');
      return true;
    } catch (error) {
      console.error('Error sending password reset email:', error);
      throw error;
    }
  },
  
  // Update user password
  updatePassword: async (newPassword: string) => {
    try {
      console.log('Updating user password...');
      
      const { data, error } = await supabase.auth.updateUser({
        password: newPassword
      });
      
      if (error) {
        console.error('Error updating password with Supabase:', error);
        throw error;
      }
      
      console.log('Password updated successfully');
      
      // Refresh the session to ensure all user data is up to date
      console.log('Refreshing user session after password update');
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session) {
        // Force refresh the subscription status after password change
        try {
          console.log('Refreshing subscription status after password change');
          const { refreshSubscription } = await import('@/contexts/SubscriptionContext');
          const subStatus = await refreshSubscription(true); // Force refresh
          console.log('Subscription status refreshed:', subStatus);
          return { data, subscriptionStatus: subStatus };
        } catch (subError) {
          console.error('Error refreshing subscription status after password change:', subError);
          // Don't fail the password update if subscription refresh fails
          return { data, subscriptionStatus: null, subscriptionError: subError };
        }
      } else {
        console.warn('No active session found after password update');
        return { data, subscriptionStatus: null };
      }
    } catch (error) {
      console.error('Error updating password:', error);
      throw error;
    }
  },
  
  // Get user
  getUser: async () => {
    try {
      const { data, error } = await supabase.auth.getUser();
      if (error) throw error;
      return data.user;
    } catch (error) {
      if (env.DEV) console.error('Get user error');
      return null;
    }
  },
  
  // Update user
  updateUser: async (attributes: { email?: string; password?: string; data?: object }) => {
    try {
      const { data, error } = await supabase.auth.updateUser(attributes);
      if (error) throw error;
      return data;
    } catch (error) {
      if (env.DEV) console.error('Update user error');
      throw error;
    }
  },
  
  // Sign up
  signUp: async (email: string, password: string) => {
    try {
      // Ensure the app URL is properly formatted
      const appUrl = (env.VITE_APP_URL || 'https://www.coachvault.co.uk').replace(/\/$/, '');
      const redirectUrl = `${appUrl}/verify`;
      
      if (env.DEV) {
        console.log(`Using app URL for signup: ${appUrl}`);
        console.log(`Signup redirect URL: ${redirectUrl}`);
      }
      
      // First, set up the email verification flow
      if (typeof window !== 'undefined' && window.setupEmailVerification) {
        await window.setupEmailVerification();
      }
      
      // Then, proceed with the signup
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          // Use the full URL with protocol for the redirect
          emailRedirectTo: redirectUrl,
          data: {
            // Add any additional user metadata here
            signup_method: 'email',
            email_verified: false,
            // Store the redirect URL in user metadata for reference
            signup_redirect_url: redirectUrl,
            // Add timestamp for tracking
            signup_timestamp: new Date().toISOString()
          }
        }
      });
      
      if (env.DEV) {
        console.log('Signup response:', { data, error });
      }
      
      if (error) {
        console.error('Signup error details:', error);
        throw error;
      }
      
      // Store verification state in localStorage
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('auth_flow', 'signup');
        localStorage.setItem('email_confirmation_pending', 'true');
        localStorage.setItem('pending_email', email);
      }
      
      return data;
    } catch (error) {
      console.error('Sign up error:', error);
      throw error;
    }
  }
};
